// src/utils/auth.js
import {
  signIn, signOut, signUp,
  confirmSignIn, fetchAuthSession, getCurrentUser,
} from "@aws-amplify/auth";
import { AmplifyEventBus } from "aws-amplify-vue";
import cookieService from "../services/cookie-service";

/**
 * Get the currently authenticated user.
 */
async function getUser() {
  console.log("getUser called");
  try {
    const user = await getCurrentUser();
    return user || null;
  } catch (err) {
    console.log("getUser error:", err);
    return null;
  }
}

/**
 * Sign up a new user.
 */
async function signUpUser(username, password, email, phone,khConsentGiven) {
  try {
    const { userId } = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          name: username,
          phone_number: phone,
          'custom:Origin': 'kh',
          'custom:khConsentGiven':khConsentGiven ? "1" : "0"
        },
      },
    });

    AmplifyEventBus.$emit("localUser", userId);
    return { code: "success", userId };
  } catch (err) {
    console.error("Sign up error:", err);
    throw err;
  }
}

/**
 * Sign in (handles EMAIL_OTP challenge automatically).
 */
async function signInUser(username, password) {
  console.log("signIn called");
  try {
    const { nextStep, isSignedIn } = await signIn({ username, password });

    // 🔸 If MFA required
    if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
      console.warn("❌ OTP required");
      return { code: "MFA_REQUIRED" };
    }

    // 🔹 If signed in directly
    if (isSignedIn) {
      cookieService.eraseCookie("source");
      AmplifyEventBus.$emit("authState", "signedIn");
      return { code: "success" };
    }

    console.warn("Unknown sign-in result:", nextStep);
    return { code: "unknown" };
  } catch (err) {
    console.error("Sign-in error:", err);
    switch (err.name) {
      case "UserNotConfirmedException":
        AmplifyEventBus.$emit("authState", "confirmSignUp");
        break;
      case "PasswordResetRequiredException":
        AmplifyEventBus.$emit("authState", "forgotPassword");
        break;
      case "NotAuthorizedException":
        console.warn("❌ Invalid credentials");
        break;
      case "UserNotFoundException":
        console.warn("❌ User not found");
        break;
      default:
        console.error(err);
    }
    return err;
  }
}

/**
 * Confirm MFA / OTP step.
 */
async function handleMfaSubmit(code) {
  try {
    const pending = localStorage.getItem("pendingUser");
    if (!pending) throw new Error("Missing pending user context");

    const { isSignedIn } = await confirmSignIn({ challengeResponse: code });
    if (!isSignedIn) throw new Error("Failed to confirm sign-in");

    await fetchAuthSession();
    localStorage.removeItem("pendingUser");
    AmplifyEventBus.$emit("authState", "signedIn");
    return { code: "success" };
  } catch (error) {
    console.error("❌ MFA failed:", error);
    return error;
  }
}

/**
 * Global sign-out.
 */
async function signOutUser() {
  try {
    await signOut({ global: true });
    AmplifyEventBus.$emit("authState", "signedOut");
    return { code: "success" };
  } catch (err) {
    console.error("Sign-out error:", err);
    return err;
  }
}

async function checkAuthSession(store) {
  console.log("[auth.js] checkAuthSession called");
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();

    // ✅ Detect federated sign-ins
    const isFederated =
      !!session?.tokens?.idToken?.payload?.identities &&
      Array.isArray(session.tokens.idToken.payload.identities);

    if (user) {
      store.commit(
        "general/setUserState",
        "signedIn"
      );
      console.log(`[auth.js] User detected (${isFederated ? "federated" : "signedIn"})`);
      return user;
    }

    store.commit("general/setUserState", "signedOut");
    return null;
  } catch (err) {
    console.warn("[auth.js] No active session", err);
    store.commit("general/setUserState", "signedOut");
    return null;
  }
}

export {
  getUser,
  checkAuthSession,
  signUpUser as signUp,
  signInUser as signIn,
  signOutUser as signOut,
  handleMfaSubmit,
};
